import {Component, OnDestroy, OnInit} from '@angular/core';
import {NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterEvent} from '@angular/router';
import {Observable, Subject} from 'rxjs';
import {map, takeUntil} from 'rxjs/operators';

import {BusyService} from '../../commons/busy.service';
import {MenuService} from '../../shared/menu.service';
import {LockoutService, LogoutService, UserProfile} from 'auth-commons';
import {LoginService} from '../../shared/login.service';
import {PermissionsService} from '../../shared/permissions.service';
import {UsersService} from '../../shared/users.service';
import {Permission, Scope} from 'smag-commons/dto/api';
import {can} from '../../shared/permissions';
import {isAdvancedUser, isRepresentativeUser} from '../../commons/commons';
import {SmagApiService} from '../../api/smag-api.service';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {

    private readonly destroyed = new Subject();

    profile: Observable<UserProfile>;
    isUserAdmin: Observable<boolean>;
    isPermAdmin: Observable<boolean>;
    isAdvUser: Observable<boolean>;
    isRepUser: Observable<boolean>;
    username: string;

    constructor(private readonly router: Router,
                private readonly busy: BusyService,
                private readonly logoutService: LogoutService,
                private readonly lockoutService: LockoutService,
                private readonly menuService: MenuService,
                private readonly loginService: LoginService,
                private readonly usersService: UsersService,
                private readonly permissionsService: PermissionsService,
                private readonly apiService: SmagApiService) {
        this.profile = null;
        this.isUserAdmin = null;
        this.isPermAdmin = null;
        this.username = null;
    }

    ngOnInit() {
        this.router.events
            .pipe(takeUntil(this.destroyed))
            .subscribe((event: RouterEvent) => {
                if (event instanceof NavigationStart) {
                    this.busy.iamBusy();
                } else if (event instanceof NavigationEnd ||
                    event instanceof NavigationError ||
                    event instanceof NavigationCancel) {
                    this.busy.iamFree();
                }
            });

        this.profile = this.loginService.userProfile.pipe(
            takeUntil(this.destroyed)
        );
        this.isUserAdmin = this.usersService.permissions.pipe(
            map(perms => can(perms, Permission.READ)), // at least, must be able to READ users
            takeUntil(this.destroyed)
        );
        this.isPermAdmin = this.permissionsService.overallPermissions.pipe(
            map(perms => can(perms, Permission.PERMISSIONS)),
            takeUntil(this.destroyed)
        );
        this.isAdvUser = isAdvancedUser(this.profile).pipe(
            map(resp => resp),
            takeUntil(this.destroyed)
        );
        this.isRepUser = isRepresentativeUser(this.profile).pipe(
            map(resp => resp),
            takeUntil(this.destroyed)
        );

        // management of firstName in case user is ADV or REP
        this.profile.subscribe(profile => {
            if (profile && profile.canOperate) {
                if (profile.scopes?.includes(Scope.ADVANCED)) {
                    this.apiService.findMemberByUser(profile.u3id)
                        .subscribe(mem => this.username = mem ? mem.personal_data.first_name : null);
                } else if (profile.scopes?.includes(Scope.REPRESENTATIVE)) {
                    this.apiService.findRepresentativeByU3id(profile.u3id)
                        .subscribe(mem => this.username = mem ? mem.personal_data.first_name : null);
                } else {  // reset username
                    this.username = null;
                }
            }
        });
    }


    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    logout() {
        this.logoutService.logout()
            .then(() => {
                this.username = null;
                this.lockoutService.lockout();
                this.loginService.notifyLogout();
            });
    }

    get menuEnabled(): Observable<boolean> {
        return this.menuService.menuEnabledStatus;
    }

    get menuShown(): Observable<boolean> {
        return this.menuService.menuShowStatus;
    }

    get entityTreeEnabled(): Observable<boolean> {
        return this.menuService.treeEnabledStatus;
    }

    get entityTreeShown(): Observable<boolean> {
        return this.menuService.treeShowStatus;
    }

    getNameFor(profile: UserProfile): string {
        return this.username || profile.firstName;
    }

    toggleMenu(showMenu: boolean) {
        if (showMenu) {
            this.menuService.showMenu();
        } else {
            this.menuService.hideMenu();
        }
    }

    toggleEntityTree(showTree: boolean) {
        if (showTree) {
            this.menuService.showTree();
        } else {
            this.menuService.hideTree();
        }
    }

    openConnectivity() {
        this.router.navigate(['/c', 'connectivity'], {queryParamsHandling: 'merge'}).then();
    }
}

